/* FSR: product recommendations in the cart drawer. Fetches the cart-drawer
   section through the product recommendations endpoint once per URL in
   data-urls (one URL per distinct cart product, pipe separated), takes the
   first card of every response, fills free slots round-robin with the next
   cards, dedupes by product id and swaps the assembled carousel in. Stays
   hidden when nothing comes back. Responses are cached per URL for the page
   lifetime because the drawer re-renders on every cart change. */
if (!customElements.get('fsr-cart-recommendations')) {
  const fsrCartRecsCache = new Map();

  const fsrCartRecsFetch = (url) => {
    if (!fsrCartRecsCache.has(url)) {
      fsrCartRecsCache.set(
        url,
        fetch(url)
          .then((response) => (response.ok ? response.text() : ''))
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const body = doc.querySelector('[data-fsr-recommendations]');
            return {
              body,
              // only the recommendation slides, not the media slides inside the cards
              slides: body ? Array.from(body.querySelectorAll('.swiper-slide[data-product-id]')) : [],
            };
          })
          .catch(() => ({ body: null, slides: [] }))
      );
    }
    return fsrCartRecsCache.get(url);
  };

  class FsrCartRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded === 'true' || !this.dataset.urls) return;
      this.dataset.loaded = 'true';

      const urls = this.dataset.urls.split('|').filter(Boolean);
      const limit = parseInt(this.dataset.limit, 10) || 3;

      Promise.all(urls.map(fsrCartRecsFetch)).then((results) => {
        if (!this.isConnected) return;

        // Pass 1 takes the first card of every line item (cart order), the
        // following passes fill the remaining slots with the second, third...
        // card of each line item. A product only ever appears once.
        const picked = [];
        const seen = new Set();
        const queues = results.map((result) => result.slides.slice());
        let progressed = true;
        while (picked.length < limit && progressed) {
          progressed = false;
          for (const queue of queues) {
            while (queue.length) {
              const slide = queue.shift();
              const id = slide.dataset.productId;
              if (id && seen.has(id)) continue;
              if (id) seen.add(id);
              picked.push(slide.cloneNode(true));
              progressed = true;
              break;
            }
            if (picked.length >= limit) break;
          }
        }
        if (!picked.length) return;

        const template = results.find((result) => result.body && result.body.querySelector('swiper-component'));
        const target = this.querySelector('[data-fsr-recommendations]');
        if (!template || !target) return;

        const body = template.body.cloneNode(true);
        const swiper = body.querySelector('swiper-component');
        const wrapper = swiper.querySelector(':scope > .swiper-wrapper');
        wrapper.innerHTML = '';
        picked.forEach((slide) => wrapper.appendChild(slide));
        try {
          const options = JSON.parse(swiper.getAttribute('data-swiper-options'));
          options.slideCount = picked.length;
          swiper.setAttribute('data-swiper-options', JSON.stringify(options));
        } catch (error) {
          // keep the rendered options if they cannot be parsed
        }

        target.innerHTML = body.innerHTML;
        this.classList.remove('hidden');
        // swiper-component only initialises itself on window load, which
        // has long passed here, so kick the carousel and the card media
        // sliders manually
        this.querySelectorAll('swiper-component').forEach((el) => {
          if (typeof el.init === 'function') el.init();
        });
      });
    }
  }
  customElements.define('fsr-cart-recommendations', FsrCartRecommendations);
}
